// User-activity timeline + historie objektu: kategorie souhrnu (Materiál, Fotky, …)
// s jednotlivými řádky pod každým nadpisem. Backend ukládá rich description do
// `change_description` (věty oddělené „ · ", viz `object-history-patch-description.ts`).

import { ActivityEntry } from '../../core/models/user-activity.model';
import type { ObjectHistoryEntry } from '../../core/models/object-history.model';
import {
  getExcavationMaterialName,
  getExcavationSubtypeName,
  getPerRowBeyondScopeLabel,
  getExcavationSurfaceBeyondScopeRows,
  getPhaseName,
  isVsoHp,
} from '@core/shared-bridge';

export interface ActivitySummaryCategory {
  /** Lokalizovaný nadpis kategorie ("Materiál", "Fotky", …). */
  label: string;
  /** Jednotlivé položky pod nadpisem — každá se vykreslí jako vlastní řádek. */
  items: string[];
}

const NOTE_PREFIXES = [
  'poznámka u objektu:',
  'popis objektu:',
  'koncept poznámky k fázi:',
  'poznámka k fázi:',
] as const;

function pick(obj: unknown, key: string): unknown {
  if (!obj || typeof obj !== 'object') return undefined;
  return (obj as Record<string, unknown>)[key];
}

function splitRichDescription(raw: unknown): string[] {
  if (typeof raw !== 'string' || !raw.trim()) return [];
  return raw
    .split(/\s+·\s+/)
    .flatMap((chunk) => chunk.split(/\n+/))
    .map((p) => p.trim())
    .filter(Boolean);
}

function findPrefixed(parts: string[], prefix: string): string | null {
  const match = parts.find((p) => p.toLowerCase().startsWith(prefix));
  if (!match) return null;
  return match.slice(prefix.length).trim();
}

function splitList(raw: string, separator: RegExp): string[] {
  return raw
    .split(separator)
    .map((s) => s.trim())
    .filter(Boolean);
}

function pushCategory(
  categories: ActivitySummaryCategory[],
  label: string,
  items: string[],
): void {
  const clean = items.map((s) => s.trim()).filter(Boolean);
  if (clean.length === 0) return;
  categories.push({ label, items: clean });
}

/** Stručný přehled `materialUsed` (SAP × množství) — export Excel i timeline. */
export function formatMaterialUsedListForDisplay(list: unknown): string | null {
  const items = formatMaterialUsedItems(list);
  return items.length > 0 ? items.join(', ') : null;
}

export function formatMaterialUsedItems(list: unknown): string[] {
  if (!Array.isArray(list) || list.length === 0) return [];
  const items: string[] = [];
  for (const entry of list) {
    const sapRaw = pick(entry, 'sapCode');
    const qtyRaw = pick(entry, 'quantity');
    const labelRaw = pick(entry, 'label');
    const sap = typeof sapRaw === 'string' ? sapRaw.trim() : '';
    const qty = typeof qtyRaw === 'number' && Number.isFinite(qtyRaw) ? qtyRaw : undefined;
    const label = typeof labelRaw === 'string' ? labelRaw.trim() : '';
    if (!sap) continue;
    const labelPart = label ? `${label} ` : '';
    const qStr = qty != null && qty !== 1 ? ` ×${qty}` : '';
    items.push(`${labelPart}(SAP ${sap})${qStr}`);
  }
  return items;
}

function formatPovrchyFromMetadata(meta: unknown): string[] {
  if (!meta || typeof meta !== 'object' || Array.isArray(meta)) return [];
  const items: string[] = [];

  const surfaces = pick(meta, 'excavationSurfaces');
  if (Array.isArray(surfaces) && surfaces.length > 0) {
    const surfaceCount = surfaces.filter((row) => row && typeof row === 'object').length;
    const legacyRoot = pick(meta, 'excavationDeepeningCm');
    for (const row of surfaces) {
      if (!row || typeof row !== 'object') continue;
      const mat = String(pick(row, 'excavationMaterial') ?? '').trim();
      const sub = String(pick(row, 'excavationMaterialSubtype') ?? '').trim();
      const lenRaw = pick(row, 'lengthM');
      const lenN = typeof lenRaw === 'number' ? lenRaw : Number(lenRaw);
      if (!mat && !(Number.isFinite(lenN) && lenN > 0)) continue;
      const label = mat
        ? sub
          ? `${getExcavationMaterialName(mat)} — ${getExcavationSubtypeName(sub)}`
          : getExcavationMaterialName(mat)
        : 'Povrch';
      const lenPart = Number.isFinite(lenN) && lenN > 0 ? ` ${lenN} m` : '';
      const deepRaw = pick(row, 'deepeningCm');
      const deepFromRow = typeof deepRaw === 'number' ? deepRaw : Number(deepRaw);
      let deepN =
        Number.isFinite(deepFromRow) && deepFromRow > 0 ? deepFromRow : undefined;
      if (deepN == null && surfaceCount === 1) {
        const legacy =
          typeof legacyRoot === 'number' ? legacyRoot : Number(legacyRoot);
        if (Number.isFinite(legacy) && legacy > 0) deepN = legacy;
      }
      const deepSuffix = deepN != null ? ` (prohloubení ${deepN} cm)` : '';
      const beyondLabels = getExcavationSurfaceBeyondScopeRows(row as Record<string, unknown>).map((b) =>
        getPerRowBeyondScopeLabel(b.code),
      );
      const remSuffix =
        beyondLabels.length > 0 ? `, nad rámec ${beyondLabels.join('; ')}` : '';
      items.push(`${label}${lenPart}${deepSuffix}${remSuffix}`);
    }
    if (items.length > 0) return items;
  }

  const len = pick(meta, 'excavationLengthM');
  if (typeof len === 'number' && Number.isFinite(len)) items.push(`Délka výkopu: ${len} m`);
  const mat = pick(meta, 'excavationMaterial');
  if (typeof mat === 'string' && mat.trim()) {
    items.push(`Povrch: ${getExcavationMaterialName(mat.trim())}`);
  }
  const sub = pick(meta, 'excavationMaterialSubtype');
  if (typeof sub === 'string' && sub.trim()) {
    items.push(`Podtyp: ${getExcavationSubtypeName(sub.trim())}`);
  }
  const deep = pick(meta, 'excavationDeepeningCm');
  if (typeof deep === 'number' && Number.isFinite(deep) && deep > 0) {
    items.push(`Prohloubení: ${deep} cm`);
  }
  return items;
}

function formatLengthMeters(raw: unknown): string | null {
  if (raw === null || raw === undefined || raw === '') return null;
  let n: number;
  if (typeof raw === 'number') {
    n = raw;
  } else if (typeof raw === 'string') {
    n = Number(raw.replace(',', '.'));
  } else {
    return null;
  }
  return Number.isFinite(n) ? `${n} m` : null;
}

function formatSpecItems(specRaw: string): string[] {
  const parts = splitList(specRaw, /,\s*/);
  const items: string[] = [];
  let hpTotal: number | null = null;
  let hpNebyt: number | null = null;

  for (const part of parts) {
    const hpMatch = /^HP celkem\s+(\d+)/i.exec(part);
    if (hpMatch) {
      hpTotal = Number(hpMatch[1]);
      continue;
    }
    const nebytMatch = /^z toho nebyt\.?\s*HP\s+(\d+)/i.exec(part);
    if (nebytMatch) {
      hpNebyt = Number(nebytMatch[1]);
      continue;
    }
    const floorsMatch = /^(\d+)\s+podla/i.exec(part);
    if (floorsMatch) {
      items.push(`Počet pater: ${floorsMatch[1]}`);
      continue;
    }
    const flatsMatch = /^(\d+)\s+byt/i.exec(part);
    if (flatsMatch) {
      items.push(`Počet jednotek: ${flatsMatch[1]}`);
      continue;
    }
    if (part.trim()) items.push(part);
  }

  if (hpTotal != null && Number.isFinite(hpTotal)) {
    if (isVsoHp(hpTotal)) {
      items.unshift(`Počet HP: ${hpTotal}`);
    } else if (hpNebyt != null && Number.isFinite(hpNebyt)) {
      const byt = Math.max(0, hpTotal - hpNebyt);
      items.unshift(`Počet HP: ${byt}/${hpNebyt}`);
    } else {
      items.unshift(`Počet HP: ${hpTotal}`);
    }
  }

  return items;
}

function formatPhotoCountLabel(count: number): string {
  if (count === 1) return '1 nová fotka';
  if (count >= 2 && count <= 4) return `${count} nové fotky`;
  return `${count} nových fotek`;
}

function formatDeletedPhotoCountLabel(count: number): string {
  if (count === 1) return '1 smazaná fotka';
  if (count >= 2 && count <= 4) return `${count} smazané fotky`;
  return `${count} smazaných fotek`;
}

/** Align `object_history.change_type` aliases with user-activity `type` values. */
export function normalizeSummaryChangeType(type: string): string {
  if (type === 'photo_delete') return 'photo_deleted';
  return type;
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  return value as Record<string, unknown>;
}

function mergeSummaryNewValues(
  newValues: unknown,
  statusTransitionTo?: string | null,
): Record<string, unknown> | undefined {
  const merged: Record<string, unknown> = { ...asRecord(newValues) };
  if (statusTransitionTo && merged['status'] == null) {
    merged['status'] = statusTransitionTo;
  }
  return Object.keys(merged).length > 0 ? merged : undefined;
}

function collectPovrchyItems(parts: string[], povrchyFromDesc: string | null, meta: unknown): string[] {
  // Preferuj strukturované excavationSurfaces[] — description může být oříznutá (max 500).
  const fromMeta = formatPovrchyFromMetadata(meta);
  const es = meta && typeof meta === 'object' ? pick(meta, 'excavationSurfaces') : undefined;
  if (Array.isArray(es) && es.length > 0 && fromMeta.length > 0) {
    return fromMeta;
  }

  const items: string[] = [];
  if (povrchyFromDesc) {
    items.push(...splitList(povrchyFromDesc, /;\s*/));
  } else {
    items.push(...fromMeta);
  }
  for (const part of parts) {
    const trimmed = part.trim();
    if (/^prohloubení\s/i.test(trimmed) && !items.some((i) => i.toLowerCase().includes('prohloubení'))) {
      items.push(trimmed.replace(/^prohloubení\s/i, 'Prohloubení: '));
    }
  }
  return items;
}

const MATERIAL_CHANGE_TYPES = new Set(['material_added', 'material_modified', 'material_removed']);
const EXTENDED_SUMMARY_TYPES = new Set([
  'delete',
  'photo_deleted',
  'location_change',
  'warehouse_transfer',
  ...MATERIAL_CHANGE_TYPES,
]);

/** Activity types that may render a detail block under the timeline headline. */
export function activityTypeSupportsSummary(type: string): boolean {
  const changeType = normalizeSummaryChangeType(type);
  return (
    changeType === 'status_change' ||
    changeType === 'update' ||
    changeType === 'updated' ||
    changeType === 'photo_upload' ||
    EXTENDED_SUMMARY_TYPES.has(changeType)
  );
}

function formatMaterialAuditItems(newValues: unknown, changeType: string): string[] {
  if (!newValues || typeof newValues !== 'object' || Array.isArray(newValues)) return [];
  const nv = newValues as Record<string, unknown>;

  if (changeType === 'material_modified') {
    const before = nv['before'] as Record<string, unknown> | undefined;
    const after = nv['after'] as Record<string, unknown> | undefined;
    if (before && after) {
      const sap = String(after['sapCode'] ?? before['sapCode'] ?? '').trim();
      const bQty = before['quantity'];
      const aQty = after['quantity'];
      if (sap && typeof bQty === 'number' && typeof aQty === 'number') {
        return [`(SAP ${sap}) ${bQty} → ${aQty}`];
      }
    }
  }

  const sap = typeof nv['sapCode'] === 'string' ? nv['sapCode'].trim() : '';
  const qty = typeof nv['quantity'] === 'number' && Number.isFinite(nv['quantity']) ? nv['quantity'] : undefined;
  if (!sap) return [];
  const qStr = qty != null && qty !== 1 ? ` ×${qty}` : '';
  return [`(SAP ${sap})${qStr}`];
}

function buildExtendedSummaryCategories(
  changeType: string,
  details: Record<string, unknown>,
  parts: string[],
): ActivitySummaryCategory[] {
  const categories: ActivitySummaryCategory[] = [];

  if (MATERIAL_CHANGE_TYPES.has(changeType)) {
    const fromMaterialUsed = formatMaterialUsedItems(pick(details, 'materialUsed'));
    const materialFromDesc = findPrefixed(parts, 'materiál:');
    const fromDesc = materialFromDesc ? splitList(materialFromDesc, /;\s*/) : [];
    // Preferuj sloučený materialUsed (burst/batch) před jedním řádkem z description leadu.
    if (fromMaterialUsed.length > 0 && fromMaterialUsed.length >= fromDesc.length) {
      pushCategory(categories, 'Materiál', fromMaterialUsed);
    } else if (fromDesc.length > 0) {
      pushCategory(categories, 'Materiál', fromDesc);
    } else {
      pushCategory(
        categories,
        'Materiál',
        formatMaterialAuditItems(pick(details, 'newValues'), changeType),
      );
    }
  }

  if (changeType === 'photo_deleted') {
    const photoIds = pick(details, 'photoIds');
    if (Array.isArray(photoIds) && photoIds.length > 0) {
      pushCategory(categories, 'Fotky', [formatDeletedPhotoCountLabel(photoIds.length)]);
    }
    const newValues = pick(details, 'newValues');
    const photoId = pick(newValues, 'photoId');
    if (typeof photoId === 'string' && photoId.trim()) {
      pushCategory(categories, 'Detail', [`ID fotky: ${photoId.trim()}`]);
    }
  }

  if (changeType === 'location_change') {
    if (parts.length > 0) {
      pushCategory(categories, 'Poloha', parts);
    } else {
      const newValues = pick(details, 'newValues');
      const lat = pick(newValues, 'lat') ?? pick(newValues, 'latitude');
      const lng = pick(newValues, 'lng') ?? pick(newValues, 'longitude');
      if (lat != null && lng != null) {
        pushCategory(categories, 'Poloha', [`${lat}, ${lng}`]);
      }
    }
  }

  if (changeType === 'warehouse_transfer') {
    const newValues = pick(details, 'newValues');
    const fromId = pick(newValues, 'warehouseFromId');
    const toId = pick(newValues, 'warehouseToId');
    const route: string[] = [];
    if (typeof fromId === 'string' && fromId.trim()) route.push(`Odkud: ${fromId.trim()}`);
    if (typeof toId === 'string' && toId.trim()) route.push(`Kam: ${toId.trim()}`);
    pushCategory(categories, 'Sklady', route);

    const itemsRaw = pick(newValues, 'items');
    const materialLines: string[] = [];
    if (Array.isArray(itemsRaw)) {
      for (const row of itemsRaw) {
        if (!row || typeof row !== 'object') continue;
        const sap = String(pick(row, 'sapCode') ?? '').trim();
        const name = String(pick(row, 'productName') ?? '').trim();
        const qty = pick(row, 'quantity');
        const q = typeof qty === 'number' && Number.isFinite(qty) ? qty : null;
        if (!sap && !name) continue;
        const label = name || `SAP ${sap}`;
        const sapPart = sap && name ? ` (SAP ${sap})` : sap && !name ? '' : '';
        const qPart = q != null ? ` ×${q}` : '';
        materialLines.push(`${label}${sapPart}${qPart}`);
      }
    }
    if (materialLines.length > 0) {
      pushCategory(categories, 'Materiál', materialLines);
    } else if (parts.length > 0) {
      pushCategory(categories, 'Detail', parts);
    }
  }

  if (changeType === 'delete' && parts.length > 0) {
    pushCategory(categories, 'Detail', parts);
  }

  if (categories.length === 0) {
    const changeDesc = pick(details, 'changeDescription');
    if (typeof changeDesc === 'string' && changeDesc.trim()) {
      pushCategory(categories, 'Detail', splitRichDescription(changeDesc));
    }
  }

  return categories;
}

type SummarySource = {
  type?: string;
  changeType?: string;
  details: Record<string, unknown>;
};

function buildSummaryCategoriesFromSource(source: SummarySource): ActivitySummaryCategory[] {
  if (!source.details) return [];

  const categories: ActivitySummaryCategory[] = [];
  const details = source.details;
  const parts = splitRichDescription(pick(details, 'changeDescription'));

  const changeType = normalizeSummaryChangeType(
    source.type ?? (pick(details, 'changeType') as string | undefined) ?? '',
  );
  const isStatusChange = changeType === 'status_change';
  const isStructuredChange =
    changeType === 'status_change' ||
    changeType === 'update' ||
    changeType === 'updated' ||
    changeType === 'photo_upload';

  if (EXTENDED_SUMMARY_TYPES.has(changeType)) {
    return buildExtendedSummaryCategories(changeType, details, parts);
  }

  if (!isStructuredChange) return [];

  if (isStatusChange) {
    const oldStatus = (pick(details, 'statusTransitionFrom') as string | null | undefined) ?? null;
    const newValues = pick(details, 'newValues') as Record<string, unknown> | null | undefined;
    const newStatus = typeof newValues?.['status'] === 'string' ? (newValues['status'] as string) : null;
    const fromLabel = oldStatus ? getPhaseName(oldStatus) : null;
    const toLabel = newStatus ? getPhaseName(newStatus) : null;
    if (fromLabel && toLabel) {
      pushCategory(categories, 'Fáze', [`${fromLabel} → ${toLabel}`]);
    } else if (toLabel) {
      pushCategory(categories, 'Fáze', [`→ ${toLabel}`]);
    }
  }

  const materialFromDesc = findPrefixed(parts, 'materiál:');
  const fromDesc = materialFromDesc ? splitList(materialFromDesc, /;\s*/) : [];
  const fromMaterialUsed = formatMaterialUsedItems(pick(details, 'materialUsed'));
  if (fromMaterialUsed.length > 0 && fromMaterialUsed.length >= fromDesc.length) {
    pushCategory(categories, 'Materiál', fromMaterialUsed);
  } else if (fromDesc.length > 0) {
    pushCategory(categories, 'Materiál', fromDesc);
  }

  const photoIds = pick(details, 'photoIds');
  const photoCountFromIds = Array.isArray(photoIds) ? photoIds.length : 0;
  const aggregatedPhotoCount = Number(pick(details, 'aggregatedPhotoCount'));
  const photoCount =
    photoCountFromIds > 0
      ? photoCountFromIds
      : Number.isFinite(aggregatedPhotoCount) && aggregatedPhotoCount > 0
        ? aggregatedPhotoCount
        : changeType === 'photo_upload'
          ? 1
          : 0;
  if (photoCount > 0) {
    pushCategory(categories, 'Fotky', [formatPhotoCountLabel(photoCount)]);
  }

  const spec = findPrefixed(parts, 'specifikace:');
  if (spec) {
    pushCategory(categories, 'Specifikace', formatSpecItems(spec));
  }

  const svary = findPrefixed(parts, 'počet svarů:');
  if (svary) pushCategory(categories, 'Počet svarů', [svary]);

  const zafOk = findPrefixed(parts, 'zafouknuté sekundární ok:');
  if (zafOk) pushCategory(categories, 'Zafouknuté OK', [zafOk]);

  const zafukLengthFromDesc = findPrefixed(parts, 'délka záfuku:');
  if (zafukLengthFromDesc) {
    pushCategory(categories, 'Délka záfuku', [zafukLengthFromDesc]);
  } else {
    const newValuesForZafuk = pick(details, 'newValues');
    const changesForZafuk = pick(newValuesForZafuk, 'changes');
    const zafukLengthFromValues =
      formatLengthMeters(pick(changesForZafuk, 'zafukLengthM')) ??
      formatLengthMeters(pick(newValuesForZafuk, 'zafukLengthM'));
    if (zafukLengthFromValues) {
      pushCategory(categories, 'Délka záfuku', [zafukLengthFromValues]);
    }
  }

  const newValues = pick(details, 'newValues');
  const povrchyFromDesc = findPrefixed(parts, 'výkop / povrchy:');
  const povrchyItems = collectPovrchyItems(parts, povrchyFromDesc, pick(newValues, 'metadata'));
  pushCategory(categories, 'Povrchy', povrchyItems);

  const rozvadName = findPrefixed(parts, 'rozvaděč:');
  if (rozvadName) {
    pushCategory(categories, 'Rozvaděč', [rozvadName]);
  } else {
    const zafouknutoZ = findPrefixed(parts, 'zafouknuto z:');
    if (zafouknutoZ) {
      pushCategory(categories, 'Rozvaděč', [zafouknutoZ]);
    } else {
      const vazba = parts.find((p) => p.toLowerCase().startsWith('vazba na rozvaděč'));
      if (vazba) pushCategory(categories, 'Rozvaděč', [vazba]);
    }
  }

  const noteItems: string[] = [];
  for (const prefix of NOTE_PREFIXES) {
    const v = findPrefixed(parts, prefix);
    if (v) noteItems.push(...splitList(v, /\s+·\s*/));
  }
  const legacyNoteRaw = pick(newValues, 'description');
  const legacyNote =
    changeType === 'status_change' && typeof legacyNoteRaw === 'string'
      ? legacyNoteRaw.trim()
      : '';
  if (legacyNote && !noteItems.includes(legacyNote)) noteItems.push(legacyNote);
  pushCategory(categories, 'Poznámky', noteItems);

  const vrReport = findPrefixed(parts, 'technická zpráva vr:');
  if (vrReport) pushCategory(categories, 'Tech. zpráva VR', [vrReport]);

  const collab = parts.find((p) => p.toLowerCase().startsWith('spolupráce'));
  if (collab) {
    if (/odebr[aá]no/i.test(collab)) {
      pushCategory(categories, 'Spolupráce', ['odebráno']);
    } else {
      const countMatch = /(\d+)\s*e-mail/i.exec(collab);
      const count = countMatch ? Number(countMatch[1]) : NaN;
      pushCategory(categories, 'Spolupráce', [
        Number.isFinite(count) && count > 0 ? `${count} spolupracovníků` : 'ano',
      ]);
    }
  }

  return categories;
}

/**
 * Kategorie souhrnu pro timeline aktivit uživatele.
 */
export function buildActivitySummaryCategories(activity: ActivityEntry): ActivitySummaryCategory[] {
  if (!activity?.details) return [];
  return buildSummaryCategoriesFromSource({
    type: activity.type,
    details: activity.details as Record<string, unknown>,
  });
}

/**
 * Souhrn změn pro řádek polygon aktivity / historie objektu.
 * Fáze se v polygon tabulce zobrazuje ve sloupci „Změna fáze“ — neopakovat.
 */
export function buildPolygonActivitySummaryCategories(row: {
  changeType: string;
  changeDescription?: string | null;
  statusTransitionFrom?: string | null;
  statusTransitionTo?: string | null;
  materialUsed?: unknown;
  photoIds?: unknown;
  newValues?: unknown;
}): ActivitySummaryCategory[] {
  const changeType = normalizeSummaryChangeType(row.changeType);
  return buildSummaryCategoriesFromSource({
    type: changeType,
    changeType,
    details: {
      changeDescription: row.changeDescription,
      changeType,
      statusTransitionFrom: row.statusTransitionFrom,
      materialUsed: row.materialUsed,
      photoIds: row.photoIds,
      newValues: mergeSummaryNewValues(row.newValues, row.statusTransitionTo),
    },
  }).filter((c) => c.label !== 'Fáze');
}

/**
 * Kategorie souhrnu pro historii objektu (detail objektu).
 */
export function buildObjectHistorySummaryCategories(
  entry: ObjectHistoryEntry,
): ActivitySummaryCategory[] {
  if (!entry) return [];
  const changeType = normalizeSummaryChangeType(entry.changeType);
  if (!activityTypeSupportsSummary(changeType)) {
    return [];
  }
  const baseNewValues: Record<string, unknown> = { ...(entry.newValues ?? {}) };
  // Status-change ukládá výkop do `entry.metadata`; Povrchy čtou `newValues.metadata`.
  if (baseNewValues['metadata'] == null && entry.metadata && typeof entry.metadata === 'object') {
    baseNewValues['metadata'] = entry.metadata;
  }
  const mergedNewValues = mergeSummaryNewValues(baseNewValues, entry.statusTransitionTo);
  return buildSummaryCategoriesFromSource({
    type: changeType,
    changeType,
    details: {
      changeDescription: entry.description,
      changeType,
      statusTransitionFrom: entry.statusTransitionFrom,
      materialUsed: entry.materialUsed,
      photoIds: entry.photoIds,
      newValues: mergedNewValues,
    },
  });
}

/** Jedna buňka Excel: stejné souhrny jako pod záznamem na timeline. */
export function formatActivitySummaryBlockForExport(activity: ActivityEntry): string {
  if (!activityTypeSupportsSummary(activity.type)) {
    return '';
  }
  const categories = buildActivitySummaryCategories(activity);
  if (categories.length === 0) return '';
  return categories
    .map((c) => `${c.label}: ${c.items.join('; ')}`)
    .join(' · ');
}

/** SAP a množství z `details.materialUsed` (i když chybí rich description). */
export function formatActivityMaterialUsedForExport(activity: ActivityEntry): string {
  if (!activity?.details) return '';
  return formatMaterialUsedListForDisplay(pick(activity.details, 'materialUsed')) ?? '';
}

/** Polygon z API (camelCase i snake_case) pro klientské filtry na detailu uživatele / měsíci. */
export function getPolygonIdForFilter(entity: {
  polygonId?: string | null;
  polygon_id?: string | null;
}): string | null {
  const raw = entity.polygonId ?? entity.polygon_id ?? null;
  if (raw == null) return null;
  const s = String(raw).trim();
  return s.length === 0 ? null : s;
}
